/**
 * Forgot Password Form Component
 * 
 * Simple form for requesting password reset:
 * - Email input with validation
 * - Submits request to send reset email
 * - Can be pre-populated with initial email
 */

import { Button, Grid, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import * as Yup from "yup";

interface ForgotPasswordProps {
  forgotPassword: (email: string) => void;
  intialEmail: string; // Note: keeping original typo for consistency
}

const ForgotPasswordForm: React.FC<ForgotPasswordProps> = ({
  forgotPassword,
  intialEmail,
}) => {
  // Form configuration with email validation
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: intialEmail,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
    }),
    onSubmit: (values) => {
      const { email } = values;
      forgotPassword(email);
      console.log("Forgot password form submitted");
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={2}>
        {/* Instructions */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="h6" align="center">
            Please enter your email to reset your password.
          </Typography>
        </Grid>

        {/* Email Field */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            id="email"
            name="email"
            label="Email"
            variant="outlined"
            autoComplete="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </Grid>

        {/* Submit Button */}
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={{
              borderRadius: 20,
              fontWeight: 700,
              boxShadow: "0 0 18px rgba(255, 255, 255, 0.0)",
              "&:hover": {
                boxShadow: "0 0 22px rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default ForgotPasswordForm;
